import { Injectable } from '@angular/core';
import { SyncCryptService } from '../../../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../../../core/logger.service';
import {
  ERROR_CODES,
  E2eeZipFlagPayload,
  E2eeZipItem,
  ErrCode,
  FeatureFlags,
  FileItem,
  StreamingSaveDestinationOutcome,
  StreamingSaveOutcome,
  StreamingSaveSeam,
  StreamingSaveUnavailableReason,
  STREAMING_SAVE_SIZE_UNKNOWN,
  ZipFileEntry,
} from '../../../../shared/models';
import { BlendEvent } from '../../../../shared/models/enums';
import { UserService } from '../../../../core/user.service';
import { ApiService } from '../../../../core/api.service';
import { TRANSFER_STATUS } from '../../../models/transfer.model';
import { BuildTransferItemService } from '../../queue/build-transfer-item.service';
import { DownloadService } from '../download.service';
import { ZipDownloadWorkerService } from './zip-download-worker.service';
import { ExternalNavigationHandlerService } from '../../queue/external-navigation-handler.service';
import { EnumerateOptions, FolderEnumeratorService } from '../folder-enumerator.service';
import { FolderStatsCacheEntry, FolderStatsCacheService } from '../../../../core/folder-stats-cache.service';
import { sanitizePath } from '../../../../shared/helpers/sanitize-path';
import { TransferService } from '../../queue/transfer.service';
import {
  DEFAULT_ZIP_NAME,
  FOLDER_ENUMERATE_CONCURRENCY,
  FOLDER_ENUMERATE_MAX_DEPTH,
  MAX_ZIP_SIZE_FIREFOX_GB,
  MAX_ZIP_SIZE_SAFARI_GB,
  ZIP_DOWNLOAD_CONCURRENCY,
  ZIP_DOWNLOAD_MAX_BUFFERED_GB,
  ZIP_EXTENSION,
  ZIP_FILE_BLOB_FOLD_BYTES,
  ZIP_STREAM_READAHEAD_MB,
} from '../../../transfer.constants';
import { IStreamingZipWriter, IZipWriter, ZipChunkPlanItem } from '../../../models';
import { StreamingSaveSession } from '../../../../core/storage/streaming-save-session';
import { sizeBucket } from '../../../../shared/helpers/size-bucket';
import { StreamingSaveTelemetryService, streamingOutcomeForStatus } from '../../streaming-save-telemetry.service';
import { StreamingSaveService } from '../../../../core/storage/streaming-save.service';
import { ZipWriterBufferedService } from './zip-writer-buffered.service';
import { ZipWriterStreamingService } from './zip-writer-streaming.service';
import { ZipChunkReadAheadService } from './zip-chunk-read-ahead.service';
import { BlendService } from '../../../../shared/services/blend.service';
import { BrowserService } from '../../../../file/browser.service';
import { IPathData, IStoreData, ITransferItemDownload } from 'src/sync';
import { ONE_GB, ONE_MB } from '../../../../shared/shared.constants';

const ZIP_MIN_DATE = new Date(1980, 0, 1);

const FILE_DOWNLOAD_MAX_RETRIES = 2;
const FILE_DOWNLOAD_RETRY_DELAY_MS = 2000;

/** Max files per pathdata API call to avoid 413 (nginx body size limit). */
const PATHDATA_BATCH_SIZE = 500;

/** Failure codes intentionally excluded from ZIP_DOWNLOAD_FAILED telemetry (not real failures). */
const NON_REPORTED_FAILURE_CODES = [
  ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR,
  ERROR_CODES.ZIP_DOWNLOAD_SIZE_CAPPED_ERROR,
  ERROR_CODES.ZIP_DOWNLOAD_EMPTY_ERROR,
];

/** Pipeline step at which a zip failure occurred — string-valued so it serializes readably to analytics. */
enum ZipFailurePhase {
  ENUMERATE = 'enumerate',
  SIZE_CHECK = 'size-check',
  KEY_FETCH = 'key-fetch',
  KEY_DECRYPT = 'key-decrypt',
  ZIP_ASSEMBLE = 'zip-assemble',
  DOWNLOAD = 'download',
  FINALIZE = 'finalize',
}

interface PreFetchedFile {
  entry: ZipFileEntry;
  tItem: ITransferItemDownload;
  // Disk-backed (Chrome spills large Blobs to disk) so the whole file isn't pinned in the JS heap.
  blob: Blob;
}

interface ZipFailureContext {
  hasFolders: boolean;
  browser: string;
  isLink: boolean;
  streamingUsed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class E2eeZipService {
  public zipQueue: { [key: string]: E2eeZipItem } = {};
  private activeAbortFlags: { [key: string]: boolean } = {};
  private activeWriters: { [key: string]: IZipWriter } = {};
  private activeReadAheads: { [key: string]: ZipChunkReadAheadService } = {};
  private activeWaiters: { [key: string]: Map<number, { resolve: () => void; reject: (err: any) => void }> } = {};
  // Only the phase enum is kept — a raw error message could embed a decrypted file or folder
  // name that no redactor can reliably strip.
  private failureDiagnostics: { [key: string]: ZipFailurePhase } = {};
  /** Bridges the gap before a queue row exists, so a second click cannot open a second picker. */
  private isAwaitingDestination = false;

  constructor(
    private crypt: SyncCryptService,
    private buildTransferItemService: BuildTransferItemService,
    private downloadService: DownloadService,
    private zipDownloadWorkerService: ZipDownloadWorkerService,
    private folderEnumeratorService: FolderEnumeratorService,
    private folderStatsService: FolderStatsCacheService,
    private transferService: TransferService,
    private externalNavigationHandlerService: ExternalNavigationHandlerService,
    private blendService: BlendService,
    private streamingSaveTelemetryService: StreamingSaveTelemetryService,
    private log: LoggerService,
    private userService: UserService,
    private browserService: BrowserService,
    private api: ApiService,
    private streamingSaveService: StreamingSaveService,
  ) {
    this.externalNavigationHandlerService.addHandler(this.navigationHandler);
  }

  private createZipWriter(session: StreamingSaveSession | null): IZipWriter {
    return session ? new ZipWriterStreamingService(session, this.log) : new ZipWriterBufferedService();
  }

  /**
   * Enumerate selected items into zip entries. For selections containing folders,
   * reuses cached per-folder results from FolderStatsCacheService and only re-enumerates
   * folders whose cachekey has changed or whose entries aren't cached.
   */
  private async enumerateWithCache(
    selectedItems: FileItem[],
    enumConcurrency: number,
    enumMaxDepth: number,
    zipId: string,
  ): Promise<{ zipEntries: ZipFileEntry[]; failedPaths: string[] }> {
    const folders = selectedItems.filter((i) => i.type === 'dir');

    const enumOpts: EnumerateOptions = {
      concurrency: enumConcurrency,
      maxDepth: enumMaxDepth,
      isCancelled: () => this.activeAbortFlags[zipId],
    };

    if (folders.length === 0) {
      const result = await this.folderEnumeratorService.enumerateItems(selectedItems, enumOpts);
      return { zipEntries: result.entries, failedPaths: result.failedPaths };
    }

    const cachedByFolder = new Map<FileItem, FolderStatsCacheEntry | null>();
    const toScan: FileItem[] = [];
    for (const folder of folders) {
      const cached = this.folderStatsService.get(folder.sync_id, folder.cachekey);
      cachedByFolder.set(folder, cached);
      if (!cached || !cached.entries) {
        toScan.push(folder);
      }
    }

    const groupedBySyncId = new Map<number, { entries: ZipFileEntry[]; failedPaths: string[] }>();
    if (toScan.length > 0) {
      const scanResults = await this.folderEnumeratorService.enumerateItemsGrouped(toScan, enumOpts);
      for (const result of scanResults) {
        groupedBySyncId.set(result.root.sync_id, { entries: result.entries, failedPaths: result.failedPaths });
      }
      if (!this.activeAbortFlags[zipId]) {
        for (const result of scanResults) {
          if (result.entries.length === 0 || result.failedPaths.length > 0) {
            continue;
          }
          const totalBytes = result.entries.reduce((sum, e) => sum + (e.file.size || 0), 0);
          const entry: FolderStatsCacheEntry = {
            syncId: result.root.sync_id,
            cachekey: result.root.cachekey,
            totalBytes,
            fileCount: result.entries.length,
            folderCount: result.folderCount,
            failedPaths: result.failedPaths,
            entries: result.entries,
            timestamp: Date.now(),
          };
          this.folderStatsService.set(entry);
        }
      }
    }

    const orderedEntries: ZipFileEntry[] = [];
    const failedPaths: string[] = [];
    for (const item of selectedItems) {
      if (item.type !== 'dir') {
        orderedEntries.push({ file: item, relativePath: sanitizePath(item.name) });
        continue;
      }
      const cached = cachedByFolder.get(item);
      if (cached && cached.entries) {
        this.appendAll(orderedEntries, cached.entries);
        if (cached.failedPaths.length > 0) {
          this.appendAll(failedPaths, cached.failedPaths);
        }
        continue;
      }
      const fresh = groupedBySyncId.get(item.sync_id);
      if (fresh) {
        this.appendAll(orderedEntries, fresh.entries);
        if (fresh.failedPaths.length > 0) {
          this.appendAll(failedPaths, fresh.failedPaths);
        }
      }
    }

    return { zipEntries: orderedEntries, failedPaths };
  }

  private appendAll<T>(target: T[], source: T[]): void {
    source.forEach((item) => target.push(item));
  }

  private normalizeZipName(zipName: string): string {
    const trimmed = (zipName || '').trim();
    if (!trimmed) {
      return DEFAULT_ZIP_NAME;
    }
    return trimmed.toLowerCase().endsWith(ZIP_EXTENSION) ? trimmed : `${trimmed}${ZIP_EXTENSION}`;
  }

  public async createZip(selectedItems: FileItem[], zipName: string): Promise<string | undefined> {
    if (this.isProcessing()) {
      this.log.warn('[E2EE Zip] Download already in progress, ignoring request');
      return;
    }

    const archiveName = this.normalizeZipName(zipName);
    const zipId = this.crypt.getRandomHex(32);
    const hasFolders = selectedItems.some((item) => item.type === 'dir');
    // Link zips fire a separate Mixpanel event family so they can be told apart from
    // file-manager zips; properties are otherwise identical.
    const isLink = selectedItems.some((item) => !!item.linkID);
    const browser = this.browserService.getBrowserName();
    const startedAt = Date.now();

    let currentPhase: ZipFailurePhase = ZipFailurePhase.ENUMERATE;

    // A folder's total is unknowable inside the activation window, so only a dir-free selection can be gated.
    const selectionBytes = hasFolders ? undefined : selectedItems.reduce((sum, item) => sum + (item.size || 0), 0);

    // Must stay ahead of every await: the save picker needs the click's transient activation.
    const capability = this.streamingSaveService.canStream(
      StreamingSaveSeam.ZIP,
      selectionBytes ?? STREAMING_SAVE_SIZE_UNKNOWN,
      isLink,
    );
    const destinationPromise = capability.capable ? this.streamingSaveService.requestDestination(archiveName) : null;

    const streamingEvent = { seam: StreamingSaveSeam.ZIP, totalSizeBytes: selectionBytes, isLink };

    if (!capability.capable) {
      this.streamingSaveTelemetryService.track({
        ...streamingEvent,
        outcome: StreamingSaveOutcome.FELL_BACK,
        fallbackReason: capability.reason,
      });
    }

    let handle: FileSystemFileHandle | null = null;
    let session: StreamingSaveSession | null = null;
    this.isAwaitingDestination = !!destinationPromise;

    try {
      if (destinationPromise) {
        const destination = await destinationPromise;
        if (destination.outcome === StreamingSaveDestinationOutcome.CANCELLED) {
          this.streamingSaveTelemetryService.track({
            ...streamingEvent,
            outcome: StreamingSaveOutcome.PICKER_CANCELLED,
          });
          return;
        }
        if (destination.outcome === StreamingSaveDestinationOutcome.GRANTED) {
          handle = destination.handle;
          this.streamingSaveTelemetryService.track({
            ...streamingEvent,
            outcome: StreamingSaveOutcome.INITIATED,
          });
        } else {
          this.log.warn('[E2EE Zip] save picker failed, falling back to the staged path');
          this.streamingSaveTelemetryService.track({
            ...streamingEvent,
            outcome: StreamingSaveOutcome.FELL_BACK,
            fallbackReason: StreamingSaveUnavailableReason.PICKER_FAILED,
          });
        }
      }

      this.zipQueue[zipId] = {
        id: zipId,
        name: handle?.name || archiveName,
        status: TRANSFER_STATUS.STATUS_DOWNLOAD,
        totalFiles: 0,
        completedFiles: 0,
        totalBytes: 0,
        completedBytes: 0,
        progressPercent: 0,
        created: Date.now(),
        retryCount: 0,
        files: [],
      };
      this.activeAbortFlags[zipId] = false;
      this.transferService.showTransfer();

      this.blendService.track(
        isLink ? BlendEvent.SHARE_LINK_ZIP_DOWNLOAD_INITIATED : BlendEvent.ZIP_DOWNLOAD_INITIATED,
        {
          initialSelectionCount: selectedItems.length,
          hasFolders,
          browser,
          streamingUsed: !!handle,
          ...(selectionBytes !== undefined && { sizeBucket: sizeBucket(selectionBytes) }),
        },
      );

      // Step 1: Enumerate all files (resolve folders recursively, using per-folder cache)
      const flagPayload: E2eeZipFlagPayload =
        this.userService.getFeatureFlagPayload(FeatureFlags.E2EE_ZIP_DOWNLOAD) || {};
      const enumConcurrency = flagPayload?.folderEnumerateConcurrency || FOLDER_ENUMERATE_CONCURRENCY;
      const enumMaxDepth = flagPayload?.folderEnumerateMaxDepth || FOLDER_ENUMERATE_MAX_DEPTH;
      const downloadConcurrency = flagPayload?.zipDownloadConcurrency || ZIP_DOWNLOAD_CONCURRENCY;
      const maxBufferedBytes = (flagPayload?.maxBufferedGb ?? ZIP_DOWNLOAD_MAX_BUFFERED_GB) * ONE_GB;
      const readAheadBytes = (flagPayload?.streamReadAheadMb ?? ZIP_STREAM_READAHEAD_MB) * ONE_MB;

      const { zipEntries, failedPaths } = await this.enumerateWithCache(
        selectedItems,
        enumConcurrency,
        enumMaxDepth,
        zipId,
      );

      if (this.activeAbortFlags[zipId]) {
        return;
      }

      if (failedPaths.length > 0) {
        this.log.warn(`[E2EE Zip] ${failedPaths.length} folder(s) failed to enumerate`);
      }

      if (zipEntries.length === 0) {
        const code =
          failedPaths.length > 0
            ? ERROR_CODES.ZIP_DOWNLOAD_FOLDER_ENUMERATION_ERROR
            : ERROR_CODES.ZIP_DOWNLOAD_EMPTY_ERROR;
        this.setError(zipId, new ErrCode(code));
        return;
      }

      // Partial success: some files enumerated but some folders could not be read.
      if (failedPaths.length > 0) {
        this.zipQueue[zipId].skippedFolderCount = failedPaths.length;
      }

      this.zipQueue[zipId].files = zipEntries;
      this.zipQueue[zipId].totalFiles = zipEntries.length;

      // Step 2: Enforce browser-specific size limits before any pathdata fetch.
      currentPhase = ZipFailurePhase.SIZE_CHECK;
      const allFiles = zipEntries.map((entry: ZipFileEntry) => entry.file);
      const totalBytes = allFiles.reduce((sum, file) => sum + (file.size || 0), 0);
      this.zipQueue[zipId].totalBytes = totalBytes;

      const maxSizeGb = handle ? null : this.getMaxZipSizeGb(flagPayload);
      if (maxSizeGb && totalBytes > maxSizeGb * ONE_GB) {
        this.setError(zipId, new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_SIZE_CAPPED_ERROR));
        return;
      }

      // Step 3: Get file keys for all files (batched to avoid 413 on large folders)
      currentPhase = ZipFailurePhase.KEY_FETCH;
      const keyData = await this.batchGetFileKeys(allFiles, enumConcurrency, () => this.activeAbortFlags[zipId]);

      if (this.activeAbortFlags[zipId]) {
        return;
      }

      // Step 4: Batch-prepare all transfer items in parallel (key decryption)
      currentPhase = ZipFailurePhase.KEY_DECRYPT;
      const tItems = await this.batchMkDownloadItems(zipEntries, keyData, downloadConcurrency);

      if (this.activeAbortFlags[zipId]) {
        return;
      }

      // Step 5: Initialize zip writer and worker pool
      currentPhase = ZipFailurePhase.ZIP_ASSEMBLE;
      if (handle) {
        session = await this.streamingSaveService.openSession(handle);
      }
      const zipWriter = this.createZipWriter(session);
      this.activeWriters[zipId] = zipWriter;
      if (this.browserService.isSafari()) {
        this.log.warn('[E2EE Zip] Safari detected — large downloads may fail due to memory limits.');
      }
      zipWriter.init(archiveName);
      this.zipDownloadWorkerService.initPool(downloadConcurrency);

      // Step 6: Download files with pipelining — pre-fetch ahead while writing to zip
      currentPhase = ZipFailurePhase.DOWNLOAD;
      if (session) {
        await this.streamedDownloadAndWrite(
          zipId,
          zipEntries,
          tItems,
          zipWriter as IStreamingZipWriter,
          downloadConcurrency,
          readAheadBytes,
        );
      } else {
        await this.pipelinedDownloadAndWrite(
          zipId,
          zipEntries,
          tItems,
          zipWriter,
          downloadConcurrency,
          maxBufferedBytes,
        );
      }

      if (this.activeAbortFlags[zipId]) {
        await zipWriter.abort();
        return;
      }

      // Step 7: Finalize zip and trigger download
      currentPhase = ZipFailurePhase.FINALIZE;
      await zipWriter.finalize();

      this.zipQueue[zipId].status = TRANSFER_STATUS.STATUS_SUCCESS;
      this.zipQueue[zipId].progressPercent = 100;

      this.blendService.track(
        isLink ? BlendEvent.SHARE_LINK_ZIP_DOWNLOAD_COMPLETED : BlendEvent.ZIP_DOWNLOAD_COMPLETED,
        {
          fileCount: this.zipQueue[zipId].totalFiles,
          totalSizeBytes: this.zipQueue[zipId].totalBytes,
          sizeBucket: sizeBucket(this.zipQueue[zipId].totalBytes),
          hasFolders,
          browser,
          streamingUsed: !!handle,
          durationMs: Date.now() - startedAt,
        },
      );

      this.trackLinkConsume(selectedItems);
    } catch (ex) {
      await this.activeWriters[zipId]?.abort();
      // suppress error if the user explicitly cancelled
      const isCancelled = this.zipQueue[zipId]?.status === TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED;
      if (!isCancelled) {
        this.setError(zipId, ErrCode.fromException(ex));
        this.failureDiagnostics[zipId] = currentPhase;
        this.log.error('E2EE zip creation failed');
        this.log.error(ex);
      }
    } finally {
      // Kill the fetch pool before any disk work: this is what cancels in-flight chunk requests.
      this.zipDownloadWorkerService.destroyPool();
      this.activeReadAheads[zipId]?.abort();
      delete this.activeReadAheads[zipId];
      if (session && !session.isSettled) {
        await session.abort();
      } else if (!session && handle) {
        await this.streamingSaveService.discardDestination(handle);
      }
      delete this.activeAbortFlags[zipId];
      delete this.activeWriters[zipId];
      delete this.activeWaiters[zipId];
      this.isAwaitingDestination = false;
      if (handle) {
        const item = this.zipQueue[zipId];
        this.streamingSaveTelemetryService.track({
          ...streamingEvent,
          outcome: streamingOutcomeForStatus(item?.status),
          fileCount: item?.totalFiles,
          totalSizeBytes: item?.totalBytes,
          errorCode: item?.errcode?.code,
          durationMs: Date.now() - startedAt,
        });
      }
      this.trackFailure(zipId, { hasFolders, browser, isLink, streamingUsed: !!handle });
    }

    return zipId;
  }

  private trackLinkConsume(items: FileItem[]): void {
    const linkId = items.find((item) => item.linkID)?.linkID;
    if (!linkId) {
      return;
    }
    this.api.send('linkconsume', { links: [{ publink_id: linkId }] }).catch((err) => {
      this.log.warn('[E2EE Zip] linkconsume tracking failed (non-fatal)');
      this.log.warn(err);
    });
  }

  public async cancelZip(zipId: string): Promise<void> {
    this.activeAbortFlags[zipId] = true;
    if (this.zipQueue[zipId]) {
      this.zipQueue[zipId].status = TRANSFER_STATUS.STATUS_DOWNLOAD_CANCELLED;
      this.zipQueue[zipId].errcode = new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
    }
    // Reject all pending write-loop waiters to unblock the pipeline immediately
    const waiters = this.activeWaiters[zipId];
    if (waiters) {
      const cancelErr = new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
      for (const [, waiter] of waiters) {
        waiter.reject(cancelErr);
      }
      waiters.clear();
    }
    this.zipDownloadWorkerService.destroyPool();
    this.activeReadAheads[zipId]?.abort();
    await this.activeWriters[zipId]?.abort();
  }

  public retryZip(selectedItems: FileItem[], zipName: string, zipId: string): void {
    const oldEntry = this.zipQueue[zipId];
    const retryCount = oldEntry ? oldEntry.retryCount + 1 : 0;
    this.removeItem(zipId);
    this.createZip(selectedItems, zipName).then((newId) => {
      if (newId && this.zipQueue[newId]) {
        this.zipQueue[newId].retryCount = retryCount;
      }
    });
  }

  public removeItem(zipId: string): void {
    delete this.zipQueue[zipId];
    delete this.activeAbortFlags[zipId];
    delete this.failureDiagnostics[zipId];
  }

  public isProcessing(): boolean {
    return (
      this.isAwaitingDestination ||
      Object.values(this.zipQueue).some((item) => item.status === TRANSFER_STATUS.STATUS_DOWNLOAD && !item.errcode)
    );
  }

  public getQueueItems(): E2eeZipItem[] {
    return Object.values(this.zipQueue);
  }

  /**
   * Fetch file keys in batches (parallel with concurrency limit)
   * to avoid 413 (Request Entity Too Large) when the folder has thousands of files.
   */
  private async batchGetFileKeys(
    files: FileItem[],
    concurrency: number,
    isCancelled: () => boolean,
  ): Promise<IPathData> {
    if (files.length <= PATHDATA_BATCH_SIZE) {
      return this.buildTransferItemService.getFileKeys(files);
    }

    // Split into batches
    const batches: FileItem[][] = [];
    for (let i = 0; i < files.length; i += PATHDATA_BATCH_SIZE) {
      batches.push(files.slice(i, i + PATHDATA_BATCH_SIZE));
    }

    const merged: IPathData = { datakeys: {}, sharekeys: {}, previewtoken: '' };
    let batchIndex = 0;

    const worker = async (): Promise<void> => {
      while (batchIndex < batches.length) {
        if (isCancelled()) {
          throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
        }
        const i = batchIndex++;
        const result = await this.buildTransferItemService.getFileKeys(batches[i]);
        Object.assign(merged.datakeys, result.datakeys);
        Object.assign(merged.sharekeys, result.sharekeys);
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, batches.length) }, () => worker());
    await Promise.all(workers);
    return merged;
  }

  /**
   * Batch-prepare transfer items (key decryption) with concurrency limit.
   * This avoids serial per-file crypto overhead.
   */
  private async batchMkDownloadItems(
    entries: ZipFileEntry[],
    keyData: IPathData,
    concurrency: number,
  ): Promise<ITransferItemDownload[]> {
    const results: ITransferItemDownload[] = new Array(entries.length);
    let index = 0;

    const worker = async (): Promise<void> => {
      while (index < entries.length) {
        const i = index++;
        results[i] = await this.buildTransferItemService.mkDownloadItem(entries[i].file, keyData);
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, entries.length) }, () => worker());
    await Promise.all(workers);
    return results;
  }

  /**
   * Write files to the zip in order, pre-fetching ahead within a window bounded by both
   * `concurrency` and `maxBufferedBytes` (whichever is hit first); at least one fetch always runs.
   */
  private async pipelinedDownloadAndWrite(
    zipId: string,
    entries: ZipFileEntry[],
    tItems: ITransferItemDownload[],
    zipWriter: IZipWriter,
    concurrency: number,
    maxBufferedBytes: number,
  ): Promise<void> {
    // Pre-fetch buffer: key = entry index, value = downloaded file data
    const fetchedFiles: Map<number, PreFetchedFile> = new Map();
    const fetchErrors: Map<number, Error> = new Map();
    let fetchIndex = 0;
    let writeIndex = 0;
    let inFlight = 0;
    let inFlightBytes = 0;
    let bufferedBytes = 0;

    const fileSizeOf = (idx: number): number => entries[idx].file.size || 0;

    // Resolvers for the write loop to wait on specific file completions.
    // Registered in activeWaiters so cancelZip can reject them immediately.
    const waiters: Map<number, { resolve: () => void; reject: (err: any) => void }> = new Map();
    this.activeWaiters[zipId] = waiters;

    // Start as many fetches as the concurrency + byte budgets allow. Strict index order means the
    // file the writer awaits is always already in-flight or done, so the byte cap can't deadlock it.
    const pump = (): void => {
      if (this.activeAbortFlags[zipId] || fetchErrors.size > 0) {
        return;
      }
      while (
        fetchIndex < entries.length &&
        inFlight < concurrency &&
        (inFlight === 0 || bufferedBytes + inFlightBytes < maxBufferedBytes)
      ) {
        const idx = fetchIndex++;
        const size = fileSizeOf(idx);
        inFlight++;
        inFlightBytes += size;
        this.downloadFileWithRetry(zipId, entries[idx], tItems[idx])
          .then((preFetched) => {
            inFlight--;
            inFlightBytes -= size;
            fetchedFiles.set(idx, preFetched);
            bufferedBytes += size;
            // Wake up the writer if it's waiting for this file
            if (waiters.has(idx)) {
              waiters.get(idx).resolve();
              waiters.delete(idx);
            }
            pump();
          })
          .catch((err) => {
            inFlight--;
            inFlightBytes -= size;
            fetchErrors.set(idx, err);
            // Wake up the writer with error — reject ALL pending waiters
            for (const [, waiter] of waiters) {
              waiter.reject(err);
            }
            waiters.clear();
          });
      }
    };

    pump();

    // Write files to zip in order, waiting for each to be fetched
    while (writeIndex < entries.length) {
      if (this.activeAbortFlags[zipId]) {
        return;
      }

      // Check if any fetch has failed (not just the current file's)
      if (fetchErrors.size > 0) {
        const error = fetchErrors.values().next().value;
        throw error;
      }

      // Wait for this file to be fetched if not ready yet
      if (!fetchedFiles.has(writeIndex)) {
        await new Promise<void>((resolve, reject) => {
          waiters.set(writeIndex, { resolve, reject });
        });
        // Re-check after waking up — could have been rejected by error handler
        if (fetchErrors.size > 0) {
          const error = fetchErrors.values().next().value;
          throw error;
        }
      }

      const preFetched = fetchedFiles.get(writeIndex);
      fetchedFiles.delete(writeIndex); // Free memory

      // Release the budget only once the writer has accepted the file, so a slow sink backpressures prefetch.
      await this.writeFileToZip(zipWriter, preFetched);
      bufferedBytes -= fileSizeOf(writeIndex);

      this.zipQueue[zipId].completedFiles++;
      this.updateProgress(zipId);

      writeIndex++;
      // Writing freed buffer space — try to start more fetches.
      pump();
    }
  }

  /**
   * Download one file's data, retrying the whole file a few times on transient failure (on top
   * of fetchFile's per-chunk retry) so a single flaky file doesn't abort the entire zip.
   * User cancellation (abort flag / ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR) is never retried.
   */
  private async downloadFileWithRetry(
    zipId: string,
    entry: ZipFileEntry,
    tItem: ITransferItemDownload,
  ): Promise<PreFetchedFile> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= FILE_DOWNLOAD_MAX_RETRIES; attempt++) {
      if (this.activeAbortFlags[zipId]) {
        throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
      }
      try {
        return await this.downloadFileData(zipId, entry, tItem);
      } catch (err) {
        lastErr = err;
        const isCancelled =
          this.activeAbortFlags[zipId] ||
          (err instanceof ErrCode && err.code === ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
        if (isCancelled || attempt >= FILE_DOWNLOAD_MAX_RETRIES) {
          break;
        }
        this.log.warn(`[E2EE Zip] a file download failed (attempt ${attempt + 1}), retrying`);
        await this.delay(FILE_DOWNLOAD_RETRY_DELAY_MS);
      }
    }
    throw lastErr;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Download and decrypt all chunks of a single file, returning the buffered result.
   */
  private async downloadFileData(
    zipId: string,
    entry: ZipFileEntry,
    tItem: ITransferItemDownload,
  ): Promise<PreFetchedFile> {
    const encFileSize = tItem.enc_filesize || 0;
    const chunkSize = this.downloadService.chunksize;
    let offset = 0;

    // Fold chunks into a disk-backed Blob as we go so per-file resident RAM stays bounded
    // regardless of file size; only the most recent `pending` chunks are held raw.
    let blob = new Blob([]);
    let pending: Uint8Array[] = [];
    let pendingBytes = 0;

    const fold = (): void => {
      if (pending.length === 0) {
        return;
      }
      blob = new Blob([blob, ...pending]);
      pending = [];
      pendingBytes = 0;
    };

    while (offset < encFileSize) {
      if (this.activeAbortFlags[zipId]) {
        throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CANCELLED_ERROR);
      }

      const remaining = encFileSize - offset;
      const reqChunk = remaining > chunkSize ? chunkSize : remaining;

      const result: IStoreData = await this.zipDownloadWorkerService.getChunk(tItem, offset, reqChunk);

      if (result.chunkLength === 0) {
        this.log.error('Chunk length was 0 during zip download');
        throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
      }

      // Worker returns Uint8Array for single-packet or Uint8Array[] for multi-packet chunks
      const rawData = result.bytearray;
      const rawPackets: ArrayBufferLike[] = Array.isArray(rawData)
        ? (rawData as unknown as ArrayBufferLike[])
        : [rawData as unknown as ArrayBufferLike];
      for (const packet of rawPackets) {
        const bytes = new Uint8Array(packet);
        pending.push(bytes);
        pendingBytes += bytes.byteLength;
        this.creditProgress(zipId, bytes.byteLength);
      }
      if (pendingBytes >= ZIP_FILE_BLOB_FOLD_BYTES) {
        fold();
      }
      offset += result.chunkLength;
    }

    fold();
    return { entry, tItem, blob };
  }

  /**
   * Write a pre-fetched file's data into the zip writer.
   */
  private async writeFileToZip(zipWriter: IZipWriter, preFetched: PreFetchedFile): Promise<void> {
    await zipWriter.addFileBlob(
      preFetched.entry.relativePath,
      preFetched.blob,
      this.zipEntryDate(preFetched.entry.file),
    );
  }

  private zipEntryDate(file: FileItem): Date {
    const rawModified = file.usertime > 0 ? file.usertime : file.date > 0 ? file.date : 0;
    const fileDate = rawModified > 0 ? new Date(rawModified) : new Date();
    return fileDate >= ZIP_MIN_DATE ? fileDate : new Date();
  }

  /** Flat, strict-order plan of encrypted ranges across every entry — the unit the ring fetches. */
  private buildChunkPlan(entries: ZipFileEntry[], tItems: ITransferItemDownload[]): ZipChunkPlanItem[] {
    const plan: ZipChunkPlanItem[] = [];
    entries.forEach((entry, entryIndex): void => {
      const encFileSize = tItems[entryIndex].enc_filesize;
      if (entry.file.size > 0 && !encFileSize) {
        this.log.error(`[E2EE Zip] missing encrypted size for ${entry.relativePath}`);
        throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
      }
      for (let offset = 0; offset < encFileSize; offset += this.downloadService.chunksize) {
        const remaining = encFileSize - offset;
        plan.push({
          entryIndex,
          offset,
          encLength: remaining > this.downloadService.chunksize ? this.downloadService.chunksize : remaining,
        });
      }
    });
    return plan;
  }

  /** Yields one entry's decrypted packets. Never returns early — take() throws so a cancelled
   *  entry cannot be closed by client-zip as a valid, silently truncated one. */
  private async *entryChunks(
    zipId: string,
    readAhead: ZipChunkReadAheadService,
    entryIndex: number,
  ): AsyncGenerator<Uint8Array> {
    const range = readAhead.rangeFor(entryIndex);
    for (let chunkIndex = range.start; chunkIndex < range.end; chunkIndex++) {
      const packets = await readAhead.take(chunkIndex);
      for (const packet of packets) {
        yield packet;
      }
    }
    this.zipQueue[zipId].completedFiles++;
    this.updateProgress(zipId);
  }

  /** Streams every entry through the writer without ever materialising a file. */
  private async streamedDownloadAndWrite(
    zipId: string,
    entries: ZipFileEntry[],
    tItems: ITransferItemDownload[],
    zipWriter: IStreamingZipWriter,
    concurrency: number,
    readAheadBytes: number,
  ): Promise<void> {
    const plan = this.buildChunkPlan(entries, tItems);
    const readAhead = new ZipChunkReadAheadService(plan, tItems, concurrency, readAheadBytes, {
      log: this.log,
      getChunk: (tItem, offset, reqLength): Promise<IStoreData> =>
        this.zipDownloadWorkerService.getChunk(tItem, offset, reqLength),
      isCancelled: (): boolean => !!this.activeAbortFlags[zipId],
      creditProgress: (bytes): void => this.creditProgress(zipId, bytes),
    });
    this.activeWaiters[zipId] = readAhead.waiters;
    this.activeReadAheads[zipId] = readAhead;
    zipWriter.setCompletionGuard((): void => readAhead.assertDrained());
    readAhead.start();

    try {
      for (let entryIndex = 0; entryIndex < entries.length; entryIndex++) {
        if (this.activeAbortFlags[zipId]) {
          return;
        }
        await zipWriter.addFileStream(
          entries[entryIndex].relativePath,
          this.entryChunks(zipId, readAhead, entryIndex),
          this.zipEntryDate(entries[entryIndex].file),
        );
      }
    } catch (err) {
      readAhead.abort();
      throw err;
    }
    // Deliberately not aborted here: addFileStream resolves when the writer accepts an entry, not
    // when its bytes are consumed, so the ring must outlive this loop until finalize() has drained.
  }

  /** Bytes are credited as chunks decrypt, so one dominant file still moves the bar. */
  private creditProgress(zipId: string, bytes: number): void {
    const item = this.zipQueue[zipId];
    if (!item) {
      return;
    }
    item.completedBytes += bytes;
    this.updateProgress(zipId);
  }

  private updateProgress(zipId: string): void {
    const item = this.zipQueue[zipId];
    if (item && item.totalBytes > 0) {
      item.progressPercent = Math.round((item.completedBytes / item.totalBytes) * 100 * 100) / 100;
      if (item.progressPercent > 100) {
        item.progressPercent = 100;
      }
    }
  }

  /**
   * Max zip size in GB for the current browser, or null for no cap (Chrome/Edge unless the flag
   * payload sets one). Safari/Firefox are capped because Blobs are memory-resident there.
   */
  private getMaxZipSizeGb(flagPayload: E2eeZipFlagPayload): number | null {
    if (this.browserService.isSafari()) {
      return flagPayload?.maxZipSizeSafariGb || MAX_ZIP_SIZE_SAFARI_GB;
    }
    if (this.browserService.isFirefox()) {
      return flagPayload?.maxZipSizeFirefoxGb || MAX_ZIP_SIZE_FIREFOX_GB;
    }
    return flagPayload?.maxZipSizeGb || null;
  }

  private setError(zipId: string, errcode: ErrCode): void {
    if (this.zipQueue[zipId]) {
      this.zipQueue[zipId].errcode = errcode;
      this.zipQueue[zipId].status = TRANSFER_STATUS.STATUS_ERROR;
    }
  }

  private trackFailure(zipId: string, context: ZipFailureContext): void {
    const { hasFolders, browser, isLink, streamingUsed } = context;
    const item = this.zipQueue[zipId];
    if (!item || item.status !== TRANSFER_STATUS.STATUS_ERROR || !item.errcode) {
      delete this.failureDiagnostics[zipId];
      return;
    }
    if (NON_REPORTED_FAILURE_CODES.includes(item.errcode.code)) {
      delete this.failureDiagnostics[zipId];
      return;
    }

    const props: { [key: string]: unknown } = {
      errorCode: item.errcode.code,
      fileCount: item.totalFiles,
      totalSizeBytes: item.totalBytes,
      sizeBucket: sizeBucket(item.totalBytes),
      hasFolders,
      browser,
      streamingUsed,
    };

    if (item.errcode.code === ERROR_CODES.GENERIC_ERROR) {
      const phase = this.failureDiagnostics[zipId];
      if (phase) {
        props.failurePhase = phase;
      }
    }

    this.blendService.track(isLink ? BlendEvent.SHARE_LINK_ZIP_DOWNLOAD_FAILED : BlendEvent.ZIP_DOWNLOAD_FAILED, props);
    delete this.failureDiagnostics[zipId];
  }

  private navigationHandler = (): string | undefined => {
    if (this.isProcessing()) {
      return 'There is an unfinished zip download pending.';
    }
    return undefined;
  };
}
